import { useCallback, useState, useEffect } from 'react';

import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Divider from '@material-ui/core/Divider';
import Hidden from '@material-ui/core/Hidden';
import Skeleton from '@material-ui/lab/Skeleton';

import {
  LocationType,
  UserVaccination,
  VaccinationStatusType,
} from '../../../services/models';
import { makeLuxonDate, mockTransport } from '../../../utils';
import { dayjs } from '../../../utils/functions/date';

import Head from '../../../components/Head';
import Layout from '../../../layout';
import CancelVaccineDialog from './CancelVaccineDialog';
import { VaccineIcon } from '../../../assets/icons';
import useStyles from './styles';
import useCommonStyles from '../../../styles/common';

type TypeVaccinationData = {
  title: string;
  fields: {
    [key: string]: string | number | undefined;
  };
};
interface VaccinationData {
  building: TypeVaccinationData;
  transport: TypeVaccinationData;
  date: TypeVaccinationData;
}

const makeBuildingLocation = (vaccination: UserVaccination) =>
  vaccination.locationType === LocationType.AccreditedNetwork
    ? {
        Endereço: vaccination.vaccinationPoint?.baseIdentifier,
      }
    : {
        Cidade: vaccination.workplace?.city,
        Endereço: vaccination.workplace?.description,
      };

const makeVaccinationData = (vaccination: UserVaccination): VaccinationData => {
  return {
    date: {
      title: 'Período de Agendamento',
      fields: {
        'Dia da Semana': makeLuxonDate(vaccination.scheduledDate, {
          weekday: 'long',
        }),
        Data: makeLuxonDate(vaccination.scheduledDate),
        Horário: !!vaccination.scheduledDate
          ? dayjs(vaccination.scheduledDate).format('HH:mm')
          : '--:--:--',
      },
    },
    building: {
      title: 'Local da Vacinação',
      fields: makeBuildingLocation(vaccination),
    },
    transport: {
      title: 'Meio de Transporte',
      fields: {
        Transporte: mockTransport(vaccination.transportType),
        'Placa do Veículo': vaccination.vehicleId,
      },
    },
  };
};

const StyledSkeleton = () => (
  <Skeleton variant="rect" width="100%" height={68} />
);

interface Props {
  Vaccination: UserVaccination;
  onStatusChanged: (status: VaccinationStatusType) => void;
}

const MyVaccine = ({ Vaccination, onStatusChanged }: Props) => {
  const commonClasses = useCommonStyles();

  const [cancelDialogIsOpen, setCancelDialogIsOpen] = useState(false);
  const [vaccination, setVaccination] = useState<UserVaccination>(Vaccination);
  const [formattedVaccination, setFormattedVaccination] =
    useState<VaccinationData | null>(null);
  const classes = useStyles({ image: VaccineIcon });

  useEffect(() => {
    setVaccination(Vaccination);
    setFormattedVaccination(makeVaccinationData(Vaccination));
  }, [Vaccination]);

  const shouldShowCancel = useCallback(
    () => vaccination.status === VaccinationStatusType.Scheduled,
    [vaccination.status]
  );

  const handleCancelDialog = useCallback(
    () => setCancelDialogIsOpen((value) => !value),
    []
  );

  const handleTitleStatus = useCallback(
    (vaccination_status: VaccinationStatusType) => {
      switch (vaccination_status) {
        case VaccinationStatusType.Canceled:
          return (
            <Typography id="cancelled-title" variant="h2">
              Vacinação Cancelada
            </Typography>
          );
        case VaccinationStatusType.Rejected:
          return (
            <Typography id="cancelled-title" variant="h2">
              Vacinação Rejeitada
            </Typography>
          );
        case VaccinationStatusType.Accepted:
          return <Typography variant="h2">Vacinação Aceita</Typography>;
        case VaccinationStatusType.Attended:
          return <Typography variant="h2">Vacinação Finalizada</Typography>;
        default:
          return <Typography variant="h2">Vacinação Contra Gripe</Typography>;
      }
    },
    []
  );

  return (
    <Layout page="myHealth" hideBackButton innerPage>
      <Head>Vacinação Contra Gripe</Head>
      <Box component="section" className={classes.container}>
        <Box>
          <Hidden only="xs">{handleTitleStatus(vaccination.status)}</Hidden>

          <Box component="section" className={classes.contentWrapper}>
            <Box className={classes.contentHeader}>
              <Grid container item xs={12} spacing={2}>
                <Hidden smDown>
                  {!!formattedVaccination?.date ? (
                    Object.entries(formattedVaccination.date.fields).map(
                      ([key, value]) => (
                        <Grid key={key} item xs>
                          <Typography variant="h3">{key}</Typography>
                          <Typography>{value}</Typography>
                        </Grid>
                      )
                    )
                  ) : (
                    <StyledSkeleton />
                  )}
                </Hidden>
                <Hidden smUp>
                  <Typography variant="h2">
                    {handleTitleStatus(vaccination.status)}
                  </Typography>
                </Hidden>
              </Grid>
            </Box>

            <Grid container className={classes.dataContainer} spacing={2}>
              <Hidden mdUp>
                <Grid item xs={12} md={6}>
                  <Typography variant="h3">
                    {formattedVaccination?.date.title}
                  </Typography>
                  <Grid container item xs={12} md={10} lg={8} spacing={2}>
                    {!!formattedVaccination?.date ? (
                      Object.entries(formattedVaccination.date.fields).map(
                        ([key, value]) => (
                          <Grid key={key} item xs>
                            <Typography variant="h4">{key}</Typography>
                            <Typography>{value}</Typography>
                          </Grid>
                        )
                      )
                    ) : (
                      <StyledSkeleton />
                    )}
                  </Grid>
                </Grid>
              </Hidden>

              <Hidden mdUp>
                <Grid item xs={12}>
                  <Box py={2}>
                    <Divider />
                  </Box>
                </Grid>
              </Hidden>

              <Grid item xs={12} md>
                <Typography variant="h3">
                  {formattedVaccination?.building.title}
                </Typography>
                <Grid container item xs={12} md={10} lg={8} spacing={2}>
                  {!!formattedVaccination?.building ? (
                    Object.entries(formattedVaccination.building.fields).map(
                      ([key, value]) =>
                        !!value && (
                          <Grid
                            key={key}
                            item
                            xs={key === 'Endereço' ? 8 : 4}
                            md
                          >
                            <Typography variant="h4">{key}</Typography>
                            <Typography>{value}</Typography>
                          </Grid>
                        )
                    )
                  ) : (
                    <StyledSkeleton />
                  )}
                </Grid>
              </Grid>

              <Hidden mdUp>
                <Grid item xs={12}>
                  <Box py={2}>
                    <Divider />
                  </Box>
                </Grid>
              </Hidden>
              <Grid item xs={12} md>
                {!!vaccination.transportType && (
                  <Typography variant="h3">
                    {formattedVaccination?.transport.title}
                  </Typography>
                )}
                <Grid container item xs={12} md={11} lg={8}>
                  {!!formattedVaccination?.transport ? (
                    Object.entries(formattedVaccination.transport.fields).map(
                      ([key, value]) =>
                        !!value && (
                          <Grid key={key} item xs>
                            <Typography variant="h4">{key}</Typography>
                            <Typography>{value}</Typography>
                          </Grid>
                        )
                    )
                  ) : (
                    <StyledSkeleton />
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </Box>

        {shouldShowCancel() && (
          <Grid container className={classes.buttonContainer}>
            <Button
              data-cy="cancel-dialog-button"
              onClick={handleCancelDialog}
              className={commonClasses.outlinedButton}
            >
              Cancelar Agendamento
            </Button>
          </Grid>
        )}
      </Box>

      <CancelVaccineDialog
        isOpen={cancelDialogIsOpen}
        handleClose={handleCancelDialog}
        onCancel={() => onStatusChanged(VaccinationStatusType.Canceled)}
        userVaccinationId={vaccination.id}
      />
    </Layout>
  );
};

export default MyVaccine;
